import * as React from 'react';
import {connect} from 'react-redux';

import * as ramda from 'ramda';

import {convertToArray} from 'nutshell-core/filter';
import {Routing} from 'nutshell-core/routing';
import type {FilterObject} from 'nutshell-core/types';

import type {NutshellState} from '../../../store';

import {FeatureGatePageWrapper} from '../../limits/feature-gates/feature-gate-page-wrapper';

import {AiGeneratedBanner} from '../banner/ai-generated-banner';
import {makeHandleClickChartToZoom} from '../charts/make-handle-click-chart-to-zoom';
import {LeadsAndActivitiesReportProvider} from '../leads-and-activities-report-provider';
import {ReportChart} from '../report-chart';
import {getReportDocumentTitle, GAP_SELECT_VALUES_DAY_TO_YEAR} from '../report-constants';
import {ReportDataSummary} from '../report-data-summary';
import type {ReportURLParams, LeadsReportKey, ChartReportParams} from '../report-types';
import {requestLeadsReportData} from '../reports-actions';
import * as reportSelectors from '../reports-selectors';

import {EmailReportSummary} from './email-report-summary';
import {TemplateGroupsViaEmailReportCta} from './template-groups-via-email-report-cta';

import styles from './activity-report.module.css';

type OwnProps = {
    activeFilters: Array<FilterObject>;
    urlParams: ReportURLParams<'leads'>;
    reportKey: LeadsReportKey;
    onParamPush: (arg1: any) => any;
    onParamChange: (arg1: any, arg2: boolean) => any;
};
type StateProps = {
    chartBucketCount: number;
};
type DispatchProps = {
    fetchReportData: (arg1: LeadsReportKey, arg2: ChartReportParams, arg3: Array<any>) => any;
};

type Props = OwnProps & StateProps & DispatchProps;

function mapStateToProps(state: NutshellState): StateProps {
    return {
        chartBucketCount: reportSelectors.getCurrentReportChartBucketCount(state),
    };
}

// @ts-expect-error
function mapDispatchToProps(dispatch) {
    return {
        // @ts-expect-error
        fetchReportData: (reportKey, reportParams, filters) => {
            dispatch(requestLeadsReportData({reportKey, reportParams, filters}));
        },
    };
}

export class EmailReportComponent extends React.Component<Props> {
    componentDidMount() {
        const reportKey = this.props.reportKey;
        const reportParams = this.props.urlParams.report;
        const filters = convertToArray(this.props.urlParams.filter);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        NutClientConfig.hasEmailReport &&
            this.props.fetchReportData(reportKey, reportParams, filters);
        document.title = getReportDocumentTitle(reportKey);
    }

    // TODO: DRY this up with leads-report.js, it's the exact same (at least for now)
    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        const {
            reportKey: nextReportKey,
            urlParams: {report: nextReportParams, filter: nextFilterParams},
        } = nextProps;

        const {
            reportKey,
            urlParams: {report: reportParams, filter: filterParams},
        } = this.props;

        const isSegmented = nextReportParams.segment !== undefined;
        const reportKeyChanged = nextReportKey !== reportKey;
        const filtersChanged = !ramda.equals(nextFilterParams, filterParams);
        const gapChanged = nextReportParams.gap !== reportParams.gap;
        const segmentChanged = nextReportParams.segment !== reportParams.segment;
        const compareToChanged = nextReportParams.compareTo !== reportParams.compareTo;

        const universalPropsChanged = reportKeyChanged || filtersChanged;

        if (
            gapChanged ||
            (compareToChanged && nextReportParams.compareTo) ||
            (isSegmented && segmentChanged) ||
            universalPropsChanged
        ) {
            const nextFilters = convertToArray(nextFilterParams);
            this.props.fetchReportData(nextReportKey, nextReportParams, nextFilters);
        }
    }

    render() {
        const {urlParams, reportKey, onParamChange, onParamPush, chartBucketCount} = this.props;

        const reportParams = urlParams.report || {};
        const comparisonType = urlParams.report && urlParams.report.compareTo;
        const currentGapIndex = GAP_SELECT_VALUES_DAY_TO_YEAR.findIndex((gap) => {
            return gap.id === reportParams.gap;
        });

        const reportDataSummary = (
            <ReportDataSummary
                currentGapIndex={currentGapIndex}
                filterParams={urlParams.filter}
                reportParams={reportParams}
                reportKey={this.props.reportKey}
            />
        );

        return NutClientConfig.hasEmailReport ? (
            <div id='report-content-scroll-container' className={styles.container}>
                <TemplateGroupsViaEmailReportCta />
                {urlParams.report.id && (
                    <div className='px-16'>
                        <AiGeneratedBanner reportId={urlParams.report.id} />
                    </div>
                )}
                <LeadsAndActivitiesReportProvider>
                    <ReportChart
                        useBuiltInEmptyState={true}
                        activeFilters={this.props.activeFilters}
                        // @ts-expect-error
                        onChartClick={makeHandleClickChartToZoom(
                            currentGapIndex,
                            chartBucketCount,
                            onParamPush,
                            'period'
                        )}
                        onParamChange={onParamChange}
                        reportDataSummary={reportDataSummary}
                        reportKey={reportKey}
                        reportParams={reportParams}
                        xAxisLabel='Email sent date'
                    />
                </LeadsAndActivitiesReportProvider>
                <EmailReportSummary
                    filters={urlParams.filter}
                    comparisonType={comparisonType}
                    activeSort={urlParams.sort}
                    onParamChange={onParamChange}
                />
            </div>
        ) : (
            <FeatureGatePageWrapper
                header='Gain insights into your email activity'
                subHeader='Curious about your email activity? Discover insights that can elevate your team’s efforts!'
                bullets={[
                    'Track total emails sent and weekly averages to stay informed about your outreach efforts.',
                    'Discover which email templates get the best responses, so you can focus on what works.',
                    'Use these insights to refine your strategy and drive better results for your business.',
                ]}
                feature='emailReport'
            />
        );
    }

    /**
     * Helper function to remove the &page=${NUM} param from the search and
     * return the adjusted search string. This could easily be extracted to a
     * more generic helper method to remove any/multiple properties from a URL
     *
     * @param  {string} search      - current search string to remove param from
     * @return {string}             - New search string without page param
     */
    removePageParam = (search?: string) => {
        if (!search) return '';

        const urlParamString = search.slice(1);
        const searchParams = Routing.deparam(urlParamString);

        delete searchParams.page;

        return `?${Routing.param(searchParams)}`;
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const EmailReport = connector(EmailReportComponent);
